import React, { useEffect, useState } from "react";
import {
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  ShoppingCartIcon,
} from "@heroicons/react/24/outline";
import { DashboardViewModel } from "../../viewmodels/dashboard/DashboardViewModel";
import EmptyModal from "../../components/modals/EmptyModal";
import { normalizeApiArrayResponse } from "../../shared/utility";
import BarChart from "../../components/charts/BarChart";

interface DashboardProps {
  logout: () => void;
}

interface MetricCard {
  title: string;
  value: string | number;
  change: string;
  changeType: "positive" | "negative" | "neutral";
}

interface ActivityItem {
  type: string;
  message: string;
  time: string;
  time_ago: string;
}

interface DashboardApiResponse {
  metrics: {
    total_sales: number;
    orders_today: number;
    low_stock_count: number;
    active_orders: number;
  };
  chart_data: {
    labels: string[];
    data: number[];
  };
  top_items: Array<{
    name: string;
    sales: number;
  }>;
  top_customers: Array<{
    name: string;
    orders: number;
  }>;
  recent_activity: ActivityItem[];
}

const METRIC_ICONS: Record<
  string,
  React.ComponentType<{ className?: string; style?: React.CSSProperties }>
> = {
  "Total Sales": CurrencyDollarIcon,
  "Orders Today": ShoppingCartIcon,
  "Low Stock": ExclamationTriangleIcon,
  "Active Orders": ClipboardDocumentListIcon,
};

/** Icons for API-driven order pipeline stats (titles vary). */
const PIPELINE_METRIC_ICONS = [
  CurrencyDollarIcon,
  ShoppingCartIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentListIcon,
];

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="text-center py-8">
      <h4
        className="text-sm font-medium apollo-heading mb-1"
        style={{ color: "var(--heading-primary)" }}
      >
        {title}
      </h4>
      <p className="text-xs apollo-body" style={{ color: "var(--nav-text-secondary)" }}>
        {description}
      </p>
    </div>
  );
}

const Dashboard: React.FC<DashboardProps> = ({ logout }) => {
  const {
    getStatsSummaries,
    getProductInventoryStats,
    getTopSellingItemsStats,
    fetchTopFiveSalesPersons,
    getChartData,
    statsSummaries,
    topSellingItemsStats,
    topSalesPersonsStats,
    chartData,
    emptyModal,
    setEmptyModelOpen,
  } = DashboardViewModel(logout);

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardApiResponse | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          getStatsSummaries(),
          getProductInventoryStats(),
          getTopSellingItemsStats(),
          fetchTopFiveSalesPersons(),
          getChartData(),
        ]);
        try {
          const response = await fetch("/api/dashboard/summary", {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "Content-Type": "application/json",
            },
          });
          if (response.ok) {
            const data = await response.json();
            setDashboardData(data);
          }
        } catch (apiError) {
          console.log("Dashboard summary API not available, using existing data");
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const userName = localStorage.getItem("first_name");
  const userType = localStorage.getItem("user_type");

  const topSellingItemsList = normalizeApiArrayResponse(topSellingItemsStats);
  const topSalesPersonsList = normalizeApiArrayResponse(topSalesPersonsStats);

  const metrics: MetricCard[] = dashboardData
    ? [
        {
          title: "Total Sales",
          value: `$${dashboardData.metrics.total_sales.toLocaleString()}`,
          change: "+12% vs LM",
          changeType: "positive",
        },
        {
          title: "Orders Today",
          value: dashboardData.metrics.orders_today.toString(),
          change: "+3 vs LY",
          changeType: "positive",
        },
        {
          title: "Low Stock",
          value: dashboardData.metrics.low_stock_count.toString(),
          change: "Alert",
          changeType: "negative",
        },
        {
          title: "Active Orders",
          value: dashboardData.metrics.active_orders.toString(),
          change: "Pending",
          changeType: "neutral",
        },
      ]
    : statsSummaries.length > 0
      ? (statsSummaries as Record<string, unknown>[]).slice(0, 4).map((s, index) => {
          const row = s as {
            name?: string;
            stat?: string | number;
            quantity?: number;
            subtitle?: string;
            change?: string;
            changeType?: string;
          };
          const value =
            row.stat !== undefined && row.stat !== ""
              ? row.stat
              : row.quantity !== undefined
                ? row.quantity
                : "—";
          const ct = row.changeType;
          const changeType: MetricCard["changeType"] =
            ct === "positive" || ct === "negative" ? ct : "neutral";
          return {
            title: row.name ?? `Stat ${index + 1}`,
            value,
            change: row.change || row.subtitle || "—",
            changeType,
          };
        })
      : [
          { title: "Total Sales", value: "0", change: "No data", changeType: "neutral" as const },
          { title: "Orders Today", value: "0", change: "No data", changeType: "neutral" as const },
          { title: "Low Stock", value: "0", change: "No alerts", changeType: "positive" as const },
          {
            title: "Active Orders",
            value: "0",
            change: "No pending",
            changeType: "neutral" as const,
          },
        ];

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div
          className="content-card px-4 sm:px-6 lg:px-8 py-6 mb-8"
          style={{ borderBottom: "var(--nav-border)" }}
        >
          <h1
            className="text-2xl font-bold apollo-heading"
            style={{ color: "var(--heading-primary)" }}
          >
            Welcome back, {userName}
          </h1>
          <p
            className="text-sm mt-1 apollo-subheading"
            style={{ color: "var(--heading-secondary)" }}
          >
            Loading your business insights...
          </p>
        </div>
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="content-card p-6">
                <div className="animate-pulse">
                  <div className="h-4 rounded w-24 mb-2" style={{ backgroundColor: "#e2e8f0" }} />
                  <div className="h-8 rounded w-16 mb-2" style={{ backgroundColor: "#e2e8f0" }} />
                  <div className="h-3 rounded w-20" style={{ backgroundColor: "#e2e8f0" }} />
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            <div className="lg:col-span-2">
              <div className="content-card p-6">
                <div className="animate-pulse">
                  <div className="h-6 rounded w-32 mb-6" style={{ backgroundColor: "#e2e8f0" }} />
                  <div className="h-64 rounded" style={{ backgroundColor: "#e2e8f0" }} />
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="content-card p-6">
                <div className="animate-pulse">
                  <div className="h-6 rounded w-24 mb-4" style={{ backgroundColor: "#e2e8f0" }} />
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center space-x-3">
                        <div
                          className="w-8 h-8 rounded-full"
                          style={{ backgroundColor: "#e2e8f0" }}
                        />
                        <div
                          className="h-4 rounded flex-1"
                          style={{ backgroundColor: "#e2e8f0" }}
                        />
                        <div className="h-4 rounded w-12" style={{ backgroundColor: "#e2e8f0" }} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <div
        className="content-card px-4 sm:px-6 lg:px-8 py-6 mb-8"
        style={{ borderBottom: "var(--nav-border)" }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1
              className="text-2xl font-bold apollo-heading"
              style={{ color: "var(--heading-primary)" }}
            >
              Welcome back, {userName}
            </h1>
            <p
              className="text-sm mt-1 apollo-subheading"
              style={{ color: "var(--heading-secondary)" }}
            >
              Here's what's happening with your business today
            </p>
          </div>
          <div className="text-sm apollo-body" style={{ color: "var(--nav-text-secondary)" }}>
            Last updated:{" "}
            {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
      </div>

      <div>
        {/* stats summary sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metrics.map((metric, index) => {
            const Icon =
              METRIC_ICONS[metric.title] ??
              PIPELINE_METRIC_ICONS[index % PIPELINE_METRIC_ICONS.length];
            return (
              <div
                key={index}
                className="content-card p-6 flex items-start gap-4"
                style={{ borderLeft: "3px solid var(--nav-active-accent)" }}
              >
                {Icon && (
                  <Icon
                    className="h-6 w-6 shrink-0 mt-0.5"
                    style={{ color: "var(--nav-text-secondary)" }}
                  />
                )}
                <div className="min-w-0 flex-1">
                  <p
                    className="text-sm font-medium apollo-subheading"
                    style={{ color: "var(--heading-secondary)" }}
                  >
                    {metric.title}
                  </p>
                  <p
                    className="text-2xl font-bold mt-1 apollo-heading"
                    style={{ color: "var(--heading-primary)" }}
                  >
                    $ {metric.value}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          <div className="lg:col-span-2">
            <div className="content-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h3
                  className="text-lg font-semibold apollo-heading"
                  style={{ color: "var(--heading-primary)" }}
                >
                  Sales Trend
                </h3>
                <span
                  className="text-sm apollo-body"
                  style={{ color: "var(--nav-text-secondary)" }}
                >
                  Last 6 months
                </span>
              </div>
              <div className="h-64">
                {(() => {
                  const fromSummary = dashboardData?.chart_data;
                  const cd = chartData as {
                    dates?: string[];
                    totals?: number[];
                    labels?: string[];
                    data?: number[];
                    datasets?: Array<{
                      label?: string;
                      data?: number[];
                      backgroundColor?: string;
                      borderColor?: string;
                    }>;
                    total_sales?: number;
                  };

                  const normalizedChartData = fromSummary?.data?.length
                    ? {
                        labels: fromSummary.labels ?? [],
                        datasets: [
                          {
                            label: "Sales",
                            data: fromSummary.data,
                            backgroundColor: "#876FD4",
                            borderColor: "#876FD4",
                          },
                        ],
                      }
                    : {
                        labels: cd.labels ?? cd.dates ?? [],
                        datasets:
                          Array.isArray(cd.datasets) && cd.datasets.length > 0
                            ? cd.datasets
                            : [
                                {
                                  label: "Sales",
                                  data: cd.totals ?? cd.data ?? [],
                                  backgroundColor: "#876FD4",
                                  borderColor: "#876FD4",
                                },
                              ],
                      };

                  const hasChartData = normalizedChartData.datasets.some(
                    (dataset) => Array.isArray(dataset.data) && dataset.data.length > 0
                  );

                  if (!hasChartData) {
                    return (
                      <div className="h-full flex items-center justify-center">
                        <EmptyState
                          title="No revenue data yet"
                          description="Start making sales to see your revenue trend"
                        />
                      </div>
                    );
                  }
                  return (
                    <div className="h-full flex flex-col">
                      {cd.total_sales != null && !fromSummary && (
                        <p
                          className="text-sm apollo-body mb-2 text-end"
                          style={{ color: "var(--nav-text-secondary)" }}
                        >
                          Total sales:{" "}
                          <span
                            className="font-semibold"
                            style={{ color: "var(--heading-primary)" }}
                          >
                            ${Number(cd.total_sales).toLocaleString()}
                          </span>
                        </p>
                      )}
                      <BarChart chartData={normalizedChartData} />
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="content-card p-6">
              <h3
                className="text-lg font-semibold apollo-heading mb-4"
                style={{ color: "var(--heading-primary)" }}
              >
                Top Products
              </h3>
              {dashboardData?.top_items && dashboardData.top_items.length > 0 ? (
                <ul className="space-y-0">
                  {dashboardData.top_items.map((product, index) => (
                    <li
                      key={index}
                      className="flex items-center justify-between py-3 first:pt-0"
                      style={index > 0 ? { borderTop: "var(--surface-border)" } : undefined}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className="text-sm tabular-nums shrink-0"
                          style={{ color: "var(--nav-text-secondary)", width: "1.25rem" }}
                        >
                          {index + 1}.
                        </span>
                        <span
                          className="text-sm font-medium truncate apollo-heading"
                          style={{ color: "var(--nav-text)" }}
                        >
                          {product.name}
                        </span>
                      </div>
                      <span
                        className="text-sm shrink-0 ml-2 apollo-body"
                        style={{ color: "var(--nav-text-secondary)" }}
                      >
                        {product.sales} sales
                      </span>
                    </li>
                  ))}
                </ul>
              ) : topSellingItemsList.length > 0 ? (
                <ul className="space-y-0">
                  {topSellingItemsList
                    .filter((row) => (row as { name?: string | null }).name)
                    .slice(0, 5)
                    .map((product, index) => (
                      <li
                        key={(product as { id?: number }).id ?? index}
                        className="flex items-center justify-between py-3 first:pt-0"
                        style={index > 0 ? { borderTop: "var(--surface-border)" } : undefined}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span
                            className="text-sm tabular-nums shrink-0"
                            style={{ color: "var(--nav-text-secondary)", width: "1.25rem" }}
                          >
                            {index + 1}.
                          </span>
                          <span
                            className="text-sm font-medium truncate apollo-heading"
                            style={{ color: "var(--nav-text)" }}
                          >
                            {(product as { name?: string }).name}
                          </span>
                        </div>
                        <span
                          className="text-sm shrink-0 ml-2 apollo-body"
                          style={{ color: "var(--nav-text-secondary)" }}
                        >
                          {(product as { sales?: number; total_sold?: number; count?: number })
                            .sales ??
                            (product as { total_sold?: number }).total_sold ??
                            (product as { count?: number }).count ??
                            0}{" "}
                          sold
                        </span>
                      </li>
                    ))}
                </ul>
              ) : (
                <EmptyState
                  title="No product sales yet"
                  description="Products will appear here once you start selling"
                />
              )}
            </div>

            <div className="content-card p-6">
              <h3
                className="text-lg font-semibold apollo-heading mb-4"
                style={{ color: "var(--heading-primary)" }}
              >
                {dashboardData?.top_customers?.length ? "Top Customers" : "Top sales reps"}
              </h3>
              {dashboardData?.top_customers && dashboardData.top_customers.length > 0 ? (
                <ul className="space-y-0">
                  {dashboardData.top_customers.map((customer, index) => (
                    <li
                      key={index}
                      className="flex items-center justify-between py-3 first:pt-0"
                      style={index > 0 ? { borderTop: "var(--surface-border)" } : undefined}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className="text-sm tabular-nums shrink-0"
                          style={{ color: "var(--nav-text-secondary)", width: "1.25rem" }}
                        >
                          {index + 1}.
                        </span>
                        <span
                          className="text-sm font-medium truncate apollo-heading"
                          style={{ color: "var(--nav-text)" }}
                        >
                          {customer.name}
                        </span>
                      </div>
                      <span
                        className="text-sm shrink-0 ml-2 apollo-body"
                        style={{ color: "var(--nav-text-secondary)" }}
                      >
                        {customer.orders} orders
                      </span>
                    </li>
                  ))}
                </ul>
              ) : topSalesPersonsList.length > 0 && userType == "1" ? (
                <ul className="space-y-0">
                  {topSalesPersonsList.slice(0, 5).map((row, index) => {
                    const r = row as {
                      user_id?: number;
                      name?: string;
                      total_orders?: number;
                      sub_dollar_amount?: string | number;
                    };
                    return (
                      <li
                        key={r.user_id ?? index}
                        className="flex items-center justify-between py-3 first:pt-0"
                        style={index > 0 ? { borderTop: "var(--surface-border)" } : undefined}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span
                            className="text-sm tabular-nums shrink-0"
                            style={{ color: "var(--nav-text-secondary)", width: "1.25rem" }}
                          >
                            {index + 1}.
                          </span>
                          <span
                            className="text-sm font-medium truncate apollo-heading"
                            style={{ color: "var(--nav-text)" }}
                          >
                            {r.name}
                          </span>
                        </div>
                        <span
                          className="text-sm shrink-0 ml-2 apollo-body"
                          style={{ color: "var(--nav-text-secondary)" }}
                        >
                          {r.total_orders ?? 0} orders · $
                          {typeof r.sub_dollar_amount === "number"
                            ? r.sub_dollar_amount.toLocaleString()
                            : (r.sub_dollar_amount ?? "0")}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <EmptyState
                  title="No sales rep data yet"
                  description="Sales activity will appear here once orders are assigned"
                />
              )}
            </div>
          </div>
        </div>

        {/* maybe add recently activity table here, remomved for now */}
      </div>
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
    </div>
  );
};

export default Dashboard;
